import { useEffect, useRef, useState } from "react";

import { delayCall } from "api/util";
import { areFilterIdsEqual, normalizeId } from "utils/filterUtils";

import CategoryFacet from "./CategoryFacet";
import CollapsibleCheckboxFilter from "./CollapsibleCheckboxFilter";
import FilterSection from "./FilterSection";

import { Chip, Icon } from "@/design-system";

const EMPTY_ARRAY = [];
const FILTER_APPLY_DELAY_MS = 200;

// The backend range contract is inclusive. Product prices display to cents, so the
// one-cent edges preserve the handoff's strict "under" / "over" labels.
const PRICE_RANGES = [
  { label: "Any price", minPrice: "", maxPrice: "" },
  { label: "Under $25", minPrice: "", maxPrice: "24.99" },
  { label: "$25–$100", minPrice: "25", maxPrice: "100" },
  { label: "Over $100", minPrice: "100.01", maxPrice: "" },
];

const arraysEqual = (left = EMPTY_ARRAY, right = EMPTY_ARRAY) => {
  if (left === right) return true;
  if (left.length !== right.length) return false;

  return left.every((value, index) => value === right[index]);
};

const filtersEqual = (left, right) =>
  left.minPrice === right.minPrice &&
  left.maxPrice === right.maxPrice &&
  left.searchByName === right.searchByName &&
  arraysEqual(left.selectedCategories, right.selectedCategories) &&
  arraysEqual(left.selectedBrands, right.selectedBrands) &&
  arraysEqual(left.selectedManufacturers, right.selectedManufacturers);

const emptyFilters = () => ({
  minPrice: "",
  maxPrice: "",
  selectedCategories: [],
  selectedBrands: [],
  selectedManufacturers: [],
  searchByName: "",
});

const priceSummary = (minPrice, maxPrice) => {
  const preset = PRICE_RANGES.find(
    (range) =>
      range.minPrice === minPrice &&
      range.maxPrice === maxPrice &&
      (range.minPrice || range.maxPrice)
  );
  if (preset) return preset.label;
  if (minPrice && maxPrice) return `$${minPrice}–$${maxPrice}`;
  if (minPrice) return `$${minPrice}+`;
  if (maxPrice) return `Under $${maxPrice}`;
  return "";
};

const activeFilterCount = (filters) =>
  filters.selectedCategories.length +
  filters.selectedBrands.length +
  filters.selectedManufacturers.length +
  Number(Boolean(filters.searchByName.trim())) +
  Number(Boolean(filters.minPrice || filters.maxPrice));

export default function FilterSidebar({
  className = "",
  filters,
  onApplyFilters,
  listCategories = EMPTY_ARRAY,
  categoryTree = EMPTY_ARRAY,
  activeCategoryId = null,
  listBrands = EMPTY_ARRAY,
  listManufacturers = EMPTY_ARRAY,
  showPrice = true,
}) {
  const {
    minPrice = "",
    maxPrice = "",
    selectedCategories = EMPTY_ARRAY,
    selectedBrands = EMPTY_ARRAY,
    selectedManufacturers = EMPTY_ARRAY,
    searchByName = "",
  } = filters || {};

  const appliedFilters = {
    minPrice,
    maxPrice,
    selectedCategories,
    selectedBrands,
    selectedManufacturers,
    searchByName,
  };
  const [localFilters, setLocalFilters] = useState(appliedFilters);
  const appliedFiltersRef = useRef(appliedFilters);
  const onApplyFiltersRef = useRef(onApplyFilters);

  useEffect(() => {
    onApplyFiltersRef.current = onApplyFilters;
  }, [onApplyFilters]);

  useEffect(() => {
    const nextFilters = {
      minPrice,
      maxPrice,
      selectedCategories,
      selectedBrands,
      selectedManufacturers,
      searchByName,
    };

    setLocalFilters((previous) =>
      filtersEqual(previous, nextFilters) ? previous : nextFilters
    );

    appliedFiltersRef.current = nextFilters;
  }, [
    minPrice,
    maxPrice,
    selectedCategories,
    selectedBrands,
    selectedManufacturers,
    searchByName,
  ]);

  useEffect(() => {
    if (
      typeof onApplyFiltersRef.current !== "function" ||
      filtersEqual(localFilters, appliedFiltersRef.current)
    ) {
      return undefined;
    }

    const nextFilters = localFilters;
    return delayCall(
      () => onApplyFiltersRef.current?.(nextFilters),
      FILTER_APPLY_DELAY_MS
    );
  }, [localFilters]);

  const setFilter = (key, value) => {
    setLocalFilters((current) => ({ ...current, [key]: value }));
  };

  const handleMultiSelectChange = (filterKey, id, checked) => {
    const normalizedId = normalizeId(id);

    setLocalFilters((currentFilters) => {
      const currentIds = currentFilters[filterKey] || [];
      const alreadySelected = currentIds.some((existing) =>
        areFilterIdsEqual(existing, normalizedId)
      );
      const nextIds = checked
        ? alreadySelected
          ? currentIds
          : [...currentIds, normalizedId]
        : currentIds.filter(
            (existing) => !areFilterIdsEqual(existing, normalizedId)
          );

      return nextIds === currentIds
        ? currentFilters
        : { ...currentFilters, [filterKey]: nextIds };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
  };

  const handleClearAll = () => {
    setLocalFilters(emptyFilters());
  };

  const filterCount = activeFilterCount(localFilters);
  const currentPriceSummary = priceSummary(
    localFilters.minPrice,
    localFilters.maxPrice
  );

  return (
    <form
      className={`w-full shrink-0 overflow-hidden rounded-lg border border-line-subtle bg-card shadow-sm ${className}`}
      aria-label="Product filters"
      onSubmit={handleSubmit}
    >
      <div className="flex items-center justify-between py-[10px] pl-[var(--space-4)] pr-[var(--space-2)]">
        <h2 className="inline-flex items-center gap-[var(--space-2)] font-sans text-sm font-semibold text-ink">
          <Icon
            name="tune"
            size={17}
            weight={400}
            className="text-orange-500"
          />
          Filters
          {filterCount > 0 && (
            <span
              className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-pill bg-orange-500 px-[5px] font-sans text-2xs font-bold text-white"
              aria-label={`${filterCount} active filters`}
            >
              {filterCount}
            </span>
          )}
        </h2>

        {filterCount > 0 && (
          <button
            type="button"
            onClick={handleClearAll}
            aria-label="Clear all filters"
            title="Clear all filters"
            className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border-0 bg-transparent text-blue-700 [transition:background_var(--duration-fast),color_var(--duration-fast),transform_var(--duration-base)_var(--ease-spring)] hover:bg-neutral-100 hover:text-blue-800 active:scale-[0.9] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
          >
            <Icon name="filter_alt_off" size={19} weight={400} />
          </button>
        )}
      </div>

      {/* Product-name search is intentionally hidden from the filter rail.
      <FilterSection
        title="Search"
        summary={localFilters.searchByName.trim()}
        onClear={
          localFilters.searchByName
            ? () => setFilter("searchByName", "")
            : undefined
        }
        clearAriaLabel="Clear product search"
        bordered={false}
        contentClassName="px-[8px] pb-[4px] pt-[6px]"
      >
        <SearchInput
          placeholder="Name, MPN or description"
          aria-label="Search products by name, MPN or description"
          value={localFilters.searchByName}
          onChange={(value) => setFilter("searchByName", value)}
        />
      </FilterSection>
      */}

      {/* Two Category shapes, one at a time. `categoryTree` renders the reference's
          single-select navigation facet (see `CategoryFacet` — rows are links, the
          active category expands its subcategories in place); `listCategories` is the
          flat, already-scoped checkbox list an API hands back (today:
          `GET_CLASSIFICATIONS_BY_BRAND` on brand listings). */}
      {categoryTree.length > 0 ? (
        <CategoryFacet tree={categoryTree} activeId={activeCategoryId} />
      ) : (
        listCategories.length > 0 && (
          <CollapsibleCheckboxFilter
            title="Category"
            items={listCategories}
            idKey="class_id"
            nameKey="class_name"
            selectedIds={localFilters.selectedCategories}
            onSelectionChange={(id, checked) =>
              handleMultiSelectChange("selectedCategories", id, checked)
            }
            onClear={() => setFilter("selectedCategories", [])}
            searchable={false}
            // Category starts collapsed everywhere — same as the nav facet. The header
            // still shows a selected count and its clear × while closed.
            defaultOpen={false}
          />
        )
      )}

      {listBrands.length > 0 && (
        <CollapsibleCheckboxFilter
          title="Brand"
          items={listBrands}
          idKey="brand_id"
          nameKey="brand_name"
          selectedIds={localFilters.selectedBrands}
          onSelectionChange={(id, checked) =>
            handleMultiSelectChange("selectedBrands", id, checked)
          }
          onClear={() => setFilter("selectedBrands", [])}
          searchPlaceholder="Search brands"
          searchAriaLabel="Search brands"
        />
      )}

      {listManufacturers.length > 0 && (
        <CollapsibleCheckboxFilter
          title="Manufacturer"
          items={listManufacturers}
          idKey="manufacturer_name"
          nameKey="manufacturer_name"
          selectedIds={localFilters.selectedManufacturers}
          onSelectionChange={(id, checked) =>
            handleMultiSelectChange("selectedManufacturers", id, checked)
          }
          onClear={() => setFilter("selectedManufacturers", [])}
          searchPlaceholder="Search manufacturers"
          searchAriaLabel="Search manufacturers"
          // Collapsed by default — it's the longest list of the three and the
          // least-used entry point, so open it would push Price below the fold.
          // `CollapsibleCheckboxFilter` still auto-shows a selected count on the
          // header, so an active manufacturer filter is visible while closed.
          defaultOpen={false}
        />
      )}

      {showPrice && (
        <FilterSection
          title="Price"
          summary={currentPriceSummary}
          onClear={
            currentPriceSummary
              ? () =>
                  setLocalFilters((current) => ({
                    ...current,
                    minPrice: "",
                    maxPrice: "",
                  }))
              : undefined
          }
          clearAriaLabel="Clear price filter"
        >
          <div className="grid grid-cols-2 gap-[5px] px-[8px] pb-[2px] pt-[6px]">
            {PRICE_RANGES.map((range) => {
              const selected =
                localFilters.minPrice === range.minPrice &&
                localFilters.maxPrice === range.maxPrice;

              return (
                <Chip
                  key={range.label}
                  selectable
                  selected={selected}
                  onClick={() =>
                    setLocalFilters((current) => ({
                      ...current,
                      minPrice: range.minPrice,
                      maxPrice: range.maxPrice,
                    }))
                  }
                  className="w-full justify-center whitespace-nowrap !h-[26px] !gap-[4px] !pl-[7px] !pr-[7px] !text-2xs"
                >
                  {range.label}
                </Chip>
              );
            })}
          </div>

          <div className="flex items-center gap-[6px] px-[8px] pb-[4px] pt-[8px]">
            <input
              type="number"
              min="0"
              inputMode="decimal"
              placeholder="Min $"
              value={localFilters.minPrice}
              onChange={(event) => setFilter("minPrice", event.target.value)}
              aria-label="Minimum price"
              className="h-[30px] w-full min-w-0 rounded-pill border-[1.5px] border-line bg-card px-[12px] py-0 font-sans text-xs text-ink outline-none placeholder:text-ink-3 [transition:border-color_var(--duration-fast)] focus:border-line-strong focus-visible:[box-shadow:var(--focus-ring)]"
            />
            <span className="shrink-0 text-xs text-ink-3">–</span>
            <input
              type="number"
              min="0"
              inputMode="decimal"
              placeholder="Max $"
              value={localFilters.maxPrice}
              onChange={(event) => setFilter("maxPrice", event.target.value)}
              aria-label="Maximum price"
              className="h-[30px] w-full min-w-0 rounded-pill border-[1.5px] border-line bg-card px-[12px] py-0 font-sans text-xs text-ink outline-none placeholder:text-ink-3 [transition:border-color_var(--duration-fast)] focus:border-line-strong focus-visible:[box-shadow:var(--focus-ring)]"
            />
          </div>
        </FilterSection>
      )}
    </form>
  );
}
